/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react';
import { ApiEndpoints } from 'services/endpoints';
import { apiService } from 'services/service';

const getSafeString = (value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && value.label) return value.label;
    return '';
};

const getInventoryFinancingType = (operationalMode) => {
    const mode = getSafeString(operationalMode).toLowerCase();
    if (mode.includes('cabon') || mode.includes('carbon')) return 'cabon_finance';
    if (mode.includes('stellar') || mode.includes('stellas')) return 'stellar_bank';
    return '';
};

const useInventoryFinancingCheck = (contactId) => {
    const [customerData, setCustomerData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!contactId) return;

        let cancelled = false;

        const fetchCustomerDetails = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const queryParams = new URLSearchParams({ contact_id: contactId });
                const response = await apiService.get(
                    `${ApiEndpoints.GET_CUSTOMER_DETAILS}?${queryParams}`,
                );

                if (!cancelled) {
                    setCustomerData(response?.data?.contact);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Failed to fetch customer details');
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchCustomerDetails();

        return () => {
            cancelled = true;
        };
    }, [contactId]);

    const result = useMemo(() => {
        if (!customerData) {
            return { hasExistingApplication: false, appliedFinancingType: null };
        }

        const customer = customerData.contact || customerData;
        if (!customer || typeof customer !== 'object') {
            return { hasExistingApplication: false, appliedFinancingType: null };
        }

        const custom = {};
        if (Array.isArray(customer.custom_fields)) {
            customer.custom_fields.forEach((f) => {
                if (f?.api_name?.startsWith('cf_')) {
                    custom[f.api_name.replace('cf_', '')] = f.value;
                }
            });
        }

        const financingType = getInventoryFinancingType(custom.operational_mode);

        const hasRegFile = !!custom.business_registration_file_name;
        const hasProofFile = !!custom.proof_of_address_file_name;
        const hasStatusFile = !!custom.status_report_file_name;
        const documentCount = [hasRegFile, hasProofFile, hasStatusFile].filter(Boolean).length;

        const hasApplication = !!financingType && documentCount >= 2;

        return {
            hasExistingApplication: hasApplication,
            appliedFinancingType: financingType || null,
        };
    }, [customerData]);

    return { ...result, isLoading, error };
};

export default useInventoryFinancingCheck;
